import { useSearchParams, useNavigate, Outlet } from "react-router-dom";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { makeClientRequestId, useLocationStore } from "@/location/locationStore";

import { useOnlineStatus } from "../offline/useOnlineStatus";


import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StreetAutocomplete, { type StreetResult } from "./StreetAutocomplete";
import FacetFilter from "@/components/FacetChipFilter";
import { offlineAddressSearchResponse } from "@/search/offlineSearchResponse";
import { NONE_VALUE, NO_STATUS_VALUE } from "@/search/constants";



import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Skeleton,
  Fab,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Grid,
  InputLabel,
  IconButton,
  Tooltip,
} from "@mui/material";

// probably put this somewhere else
const STATUS_OPTIONS = [
  { value: "INTERESTED", label: "Interested, needs quote" },
  { value: "REGULAR", label: "Agreed to regular clean" },
  { value: "CUSTOMER", label: "Already a customer" },
  { value: "LEAFLET", label: "Left leaflet" },
  { value: "CALLBACK", label: "Callback requested" },
  { value: "NOT_INTERESTED", label: "Not interested" },
  { value: "OBSERVED_DIRTY", label: "Observed dirty windows" },
  { value: "QUOTED", label: "Quoted" },
  { value: "EXCLUDE_LOW_DEM", label: "Excluded – Low Demographic" },
  { value: "EXCLUDE_ACCESS", label: "Exclude – Access Issues" },
  { value: "OTHER", label: "Other" },
];
const PAGE_SIZE = 30;

type AddressPage = {
  results: any[];
  postcode_counts: { value: string; count: number }[];
  post_town_counts?: { value: string; count: number }[];
  dependent_locality_counts?: { value: string; count: number }[];
  status_counts?: { value: string; label: string; count: number }[];
  count?: number;
  pre_facet_count?: number;
  total_pages?: number;
  previous?: string | null;
  next?: string | null;
  offline?: boolean;
};


export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const queryClient = useQueryClient();

  const facetLabel = (value: string, fallback: string) =>
    value === NONE_VALUE || value === "" ? `No ${fallback}` : value;

  const primeAddressDetail = (addr: any) => {
    if (!addr?.udprn) return;

    // Seed the address detail query with search result data so the drawer
    // can render instantly while the fresh fetch runs in the background.
    const key = ["address", String(addr.udprn)];
    queryClient.setQueryData(key, (prev: any) => ({
      ...prev,
      ...addr,
      customer: addr.customer ?? prev?.customer ?? null,
      latest_note: addr.latest_note ?? prev?.latest_note ?? null,
      notes: prev?.notes ?? (addr.latest_note ? [addr.latest_note] : []),
      offline: addr.offline ?? prev?.offline ?? false,
    }));
  };

  // scroll-to-top button state
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setShowScrollTop(window.scrollY > 250);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // URL-driven params
  const street = searchParams.get("street") ?? "";
  const postcode = searchParams.get("postcode") ?? "";
  const status = searchParams.get("status") ?? "";
  const statusList = status ? status.split(",").filter(Boolean) : [];

  const keepValue = (v: string | null) =>
    v !== undefined && v !== null && v !== "";

  const postcode_facet =
    searchParams.get("postcode_facet")?.split(",").filter(keepValue) ?? [];

  const cityFacet =
    searchParams.get("post_town_facet")?.split(",").filter(keepValue) ?? [];

  const localityFacet =
    searchParams
      .get("dependent_locality_facet")
      ?.split(",")
      .filter(keepValue) ?? [];

  const status_facet =
    searchParams.get("status_facet")?.split(",").filter(keepValue) ?? [];

  const baseSearchKey = `${street}||${postcode}||${status}`;
  const baseKeyRef = useRef(baseSearchKey);
  const [cachedFacetPage, setCachedFacetPage] = useState<AddressPage | null>(null);

  const hasParams =
    searchParams.has("street") ||
    searchParams.has("postcode") ||
    searchParams.has("postcode_facet") ||
    searchParams.has("post_town_facet") ||
    searchParams.has("dependent_locality_facet") ||
    searchParams.has("status_facet") ||
    searchParams.has("status");

  // Local inputs
  const [streetInput, setStreetInput] = useState(street);
  const [postcodeInput, setPostcodeInput] = useState(postcode);
  const [statusInput, setStatusInput] = useState(statusList);

  const searchAllowed =
  (
    streetInput.trim() !== "" ||
    postcodeInput.trim() !== "" ||
    (statusInput.length > 0 &&
      !(statusInput.length === 1 && statusInput[0] === NO_STATUS_VALUE))
  );

  const requestLocation = useLocationStore((s) => s.requestLocation);
  const sendLocationPing = useLocationStore((s) => s.sendLocationPing);
  const [searchRequestId, setSearchRequestId] = useState<string | null>(null);
  const searchLocationRequestRef = useRef<string | null>(null);
  const pendingLocationRef = useRef<{ id: string; location: any | null; sent: boolean } | null>(null);
  const searchCompletedRef = useRef(false);

  const maybeSendSearchLocation = useCallback(() => {
    console.log('maybeSendSearchLocation', [online, searchCompletedRef.current, pendingLocationRef.current, searchRequestId ])
    const current = pendingLocationRef.current;
    if (!current || current.sent) return;
    if (!current.location) return;
    if (!online) return;
    if (!searchCompletedRef.current) return;
    if (!searchRequestId || searchRequestId !== current.id) return;

    current.sent = true;
    void sendLocationPing({
      context: "search",
      requestId: current.id,
      location: current.location,
      endpointOverride: `search-log/${current.id}/location/`,
      method: "patch",
    }).catch((err) => {
      console.debug("search location ping failed", err);
    });
  }, [online, searchRequestId, sendLocationPing]);

  const beginSearchLocationFlow = useCallback(() => {
    const id = makeClientRequestId();
    console.debug("beginSearchLocationFlow id", id)
    setSearchRequestId(id);
    searchLocationRequestRef.current = id;
    searchCompletedRef.current = false;
    pendingLocationRef.current = { id, location: null, sent: false };

    if (online) {
      requestLocation("search").then((res) => {
        console.debug("beginSearchLocationFlow requestLocationSearch", [searchLocationRequestRef, pendingLocationRef])
        if (searchLocationRequestRef.current !== id) return;
        if (res.state === "granted") {
          if (pendingLocationRef.current && pendingLocationRef.current.id === id) {
            pendingLocationRef.current.location = res.location;
          }
          console.debug("beginSearchLocationFlow guards passed, check maybeSendSearchLocation")
          maybeSendSearchLocation();
        }
      });
    }

    return id;
  }, [maybeSendSearchLocation, online, requestLocation]);

  const clearSearchLocationFlow = () => {
    setSearchRequestId(null);
    searchLocationRequestRef.current = null;
    pendingLocationRef.current = null;
    searchCompletedRef.current = false;
  };

  // useEffect(() => {
  //   if (hasParams && !searchRequestId) {
  //     beginSearchLocationFlow();
  //   }
  // }, [beginSearchLocationFlow, hasParams, searchRequestId]);

  

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isFetched,
  } = useInfiniteQuery<AddressPage, Error>({
    queryKey: [
      "addresses",
      street,
      postcode,
      postcode_facet,
      cityFacet,
      localityFacet,
      status_facet,
      status,
      searchRequestId,
    ],
    enabled: hasParams && !!searchRequestId,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    staleTime: 0,
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      const page = typeof pageParam === "number" ? pageParam : 1;
      const requestId = searchRequestId;


      if (!online) {
        return await offlineAddressSearchResponse({
          street,
          postcode,
          postcode_facet,
          post_town_facet: cityFacet,
          dependent_locality_facet: localityFacet,
          status_facet,
          status: statusList,
          page,
          page_size: PAGE_SIZE,
        });
      }

      const res = await api.get("address/", {
        params: {
          thoroughfare: street || undefined,
          postcode: postcode || undefined,
          postcode_facet: postcode_facet.length ? postcode_facet.join(",") : undefined,
          post_town_facet: cityFacet.length ? cityFacet.join(",") : undefined,
          dependent_locality_facet: localityFacet.length ? localityFacet.join(",") : undefined,
          status_facet: status_facet.length ? status_facet.join(",") : undefined,
          status: statusList.length ? statusList.join(",") : undefined,
          page,
          page_size: PAGE_SIZE,
          client_request_id: requestId ?? undefined,
        },
      });
      return {
        ...res.data,
        offline: false,
      };
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage?.next ? pages.length + 1 : undefined,
  });

  const handleSearch = () => {
    if (!searchAllowed) return;
    console.debug("handleSearch");
    beginSearchLocationFlow();

    const params = new URLSearchParams();
    const trimmedStreet = streetInput.trim();
    const trimmedPostcode = postcodeInput.trim();

    if (trimmedStreet) params.set("street", trimmedStreet);
    if (trimmedPostcode) params.set("postcode", trimmedPostcode);
    if (statusInput.length) params.set("status", statusInput.join(","));

    // Clear all facets by omission (no empty keys)
    setSearchParams(params);

  };

const handleStreetSelect = (street: StreetResult | null) => {
  if (!street) {
    setStreetInput("");
    setPostcodeInput("");
    setStatusInput([]);
    setSearchParams({});
    return;
  }

  const nextStreet = street.thoroughfare || "";
  const nextPostcode = street.postcodes.join(", ");

  // Update the visible inputs
  setStreetInput(nextStreet);
  setPostcodeInput(nextPostcode);

  beginSearchLocationFlow();

  // Trigger a search using the selected values immediately, avoiding stale state
  const params = new URLSearchParams();
  if (nextStreet.trim()) params.set("street", nextStreet.trim());
  if (nextPostcode.trim()) params.set("postcode", nextPostcode.trim());
  if (statusInput.length) params.set("status", statusInput.join(","));
  setSearchParams(params);
};

type FacetPatch = {
  postcode_facet?: string[];
  post_town_facet?: string[];
  dependent_locality_facet?: string[];
  status_facet?: string[];
};

const anyFacetActive =
  postcode_facet.length > 0 ||
  cityFacet.length > 0 ||
  localityFacet.length > 0 ||
  status_facet.length > 0;

const updateFacets = (patch: FacetPatch) => {
  setSearchParams(prev => {
    const params = new URLSearchParams(prev);

    if (patch.postcode_facet !== undefined) {
      if (patch.postcode_facet.length) {
        params.set("postcode_facet", patch.postcode_facet.join(","));
      } else {
        params.delete("postcode_facet");
      }
    }

    if (patch.post_town_facet !== undefined) {
      if (patch.post_town_facet.length) {
        params.set("post_town_facet", patch.post_town_facet.join(","));
      } else {
        params.delete("post_town_facet");
      }
    }

    if (patch.dependent_locality_facet !== undefined) {
      if (patch.dependent_locality_facet.length) {
        params.set(
          "dependent_locality_facet",
          patch.dependent_locality_facet.join(",")
        );
      } else {
        params.delete("dependent_locality_facet");
      }
    }

    if (patch.status_facet !== undefined) {
      if (patch.status_facet.length) {
        params.set("status_facet", patch.status_facet.join(","));
      } else {
        params.delete("status_facet");
      }
    }

    return params;
  });
};

const handlePostcodeFacetChange = (pcs: string[]) => {
  updateFacets({ postcode_facet: pcs });
};

const handlePostTownFacetChange = (towns: string[]) => {
  updateFacets({ post_town_facet: towns });
};

const handleLocalityFacetChange = (localities: string[]) => {
  updateFacets({ dependent_locality_facet: localities });
};

const handleStatusFacetChange = (statuses: string[]) => {
  updateFacets({ status_facet: statuses });
};

const handleClearAllFacets = () => {
  if (!anyFacetActive) return;
  updateFacets({
    postcode_facet: [],
    post_town_facet: [],
    dependent_locality_facet: [],
    status_facet: [],
  });
};

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || !hasParams) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fetchNextPage();
        }
      },
      { rootMargin: "300px" }
    );

    const node = loadMoreRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, hasParams]);

  const firstPage = data?.pages?.[0];
  const typedFirstPage = firstPage as AddressPage | undefined;
  const isInitialLoading = isLoading && !firstPage;
  useEffect(() => {
    if (baseSearchKey !== baseKeyRef.current) {
      setCachedFacetPage(null);
      baseKeyRef.current = baseSearchKey;
    }
  }, [baseSearchKey]);

  useEffect(() => {
    if (typedFirstPage) {
      setCachedFacetPage(typedFirstPage);
    }
  }, [typedFirstPage]);

  useEffect(() => {
    console.debug('useEffect maybeSendSearchLocation, guard: ', [isFetched, searchRequestId])
    if (isFetched && searchRequestId) {
      searchCompletedRef.current = true;
      maybeSendSearchLocation();
    }
  }, [isFetched, searchRequestId, maybeSendSearchLocation]);

  const facetPage: AddressPage | null = typedFirstPage ?? cachedFacetPage;
  const results =
    data?.pages?.flatMap((page: any) => (page as AddressPage)?.results ?? []) ?? [];
  const totalResultsCount = facetPage?.pre_facet_count;
  const filteredResultsCount =
    typeof typedFirstPage?.count === "number"
      ? typedFirstPage.count
      : typeof facetPage?.count === "number"
        ? facetPage.count
        : results.length;
  const cityFacetOptions = facetPage?.post_town_counts ?? [];
  const localityFacetOptions = facetPage?.dependent_locality_counts ?? [];
  const statusFacetOptions = facetPage?.status_counts ?? [];
  const showFacetSkeletons = isInitialLoading && !facetPage;
  const hideResultCount = isLoading || (isFetching && !isFetchingNextPage);

  return (
    <Box sx={{ p: 0, background: "#f6f6f6", minHeight: "100%" }}>
      {/* Filters */}
      <Box sx={{ p: 2 }}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            borderRadius: 3,
          }}
        >
          <Stack spacing={1.5}>
            {/* <TextField
              label="Street"
              value={streetInput}
              fullWidth
              data-lpignore="true"
              size="small"
              onChange={(e) => setStreetInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              slotProps={{
                htmlInput: {
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                }
              }}
            /> */}
            <StreetAutocomplete
  streetInput={streetInput}
  setStreetInput={setStreetInput}
  enableAutocomplete={online}
  onEnter={handleSearch}
  onStreetSelect={handleStreetSelect}
/>

<Grid container spacing={1}>
  <Grid size={6}>
    <TextField
      label="Postcode"
      value={postcodeInput}
      size="small"
      fullWidth
      onChange={(e) => setPostcodeInput(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
    />
  </Grid>

  <Grid size={6}>
    <FormControl fullWidth size="small">
      <InputLabel>Status</InputLabel>
      <Select
        multiple
        value={statusInput}
        onChange={(e) => setStatusInput(e.target.value as string[])}
        label="Status"
        size="small"
        renderValue={(selected) => selected
          .map(v => v === NO_STATUS_VALUE ? "No Status" : STATUS_OPTIONS.find(o => o.value === v)?.label)
          .join(", ")}
      >
        <MenuItem value={NO_STATUS_VALUE}>
          <em>No Status</em>
        </MenuItem>

        {STATUS_OPTIONS.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>
</Grid>

            {/* <TextField
              label="Postcode"
              value={postcodeInput}
              fullWidth
              size="small"
              onChange={(e) => setPostcodeInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              slotProps={{
                htmlInput: {
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                }
              }}
            /> */}

            <Button variant="contained" fullWidth onClick={handleSearch}>
              Search
            </Button>
            <Button
  fullWidth
  variant="outlined"
  onClick={() => {
    setStreetInput("");
    setPostcodeInput("");
    setStatusInput([]);
    setSearchParams({});
    clearSearchLocationFlow();
  }}
>
  Clear
</Button>

          </Stack>
        </Paper>
      </Box>


<Box sx={{ px: 2, pb: 1, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
  {showFacetSkeletons ? (
    <>
      <Skeleton variant="rounded" width={140} height={34} />
      <Skeleton variant="rounded" width={140} height={34} />
    </>
  ) : (
    <>
      {facetPage?.postcode_counts && facetPage.postcode_counts.length > 0 && (
        <FacetFilter
          title="Postcode"
          options={facetPage.postcode_counts.map((p: any) => ({
            value: p.value,
            label: `${p.value} (${p.count})`,
          }))}
          selected={postcode_facet}
          onChange={handlePostcodeFacetChange}
        />
      )}

      {cityFacetOptions.length > 0 && (
        <FacetFilter
          title="City"
          options={cityFacetOptions.map((pt: any) => {
            const value = pt?.value ?? NONE_VALUE;
            return {
              value,
              label: `${facetLabel(value, "city")} (${pt?.count ?? 0})`,
            };
          })}
          selected={cityFacet}
          onChange={handlePostTownFacetChange}
        />
      )}

      {localityFacetOptions.length > 0 && (
        <FacetFilter
          title="Locality"
          options={localityFacetOptions.map((loc: any) => {
            const value = loc?.value ?? NONE_VALUE;
            return {
              value,
              label: `${facetLabel(value, "locality")} (${loc?.count ?? 0})`,
            };
          })}
          selected={localityFacet}
          onChange={handleLocalityFacetChange}
        />
      )}

      {statusFacetOptions.length > 0 && (
        <FacetFilter
          title="Status"
          options={statusFacetOptions.map((s: any) => ({
            value: s?.value ?? "",
            label: `${s?.label ?? facetLabel(s?.value ?? "", "status")} (${s?.count ?? 0})`,
          }))}
          selected={status_facet}
          onChange={handleStatusFacetChange}
        />
      )}
    </>
  )}

  {anyFacetActive && (
    <Tooltip title="Clear all facet filters">
      <IconButton
        size="small"
        onClick={handleClearAllFacets}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          ml: 0.5,
        }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )}

</Box>
        

      {/* Loading skeleton */}
      {isLoading && (
        <Box sx={{ px: 2 }}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="rounded" height={64} />
            </Box>
          ))}
        </Box>
      )}


      {/* Result count */}
      {!hideResultCount && typeof totalResultsCount === "number" && facetPage && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2, pb: 1 }}
        >
          {!anyFacetActive
            ? `${totalResultsCount} results found`
            : `Showing ${filteredResultsCount} results from ${totalResultsCount}`}
            
        </Typography>
      )}
      {typedFirstPage?.offline && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ px: 2, mb: 0.5, fontWeight: 600 }}
        >
          Offline Results!
        </Typography>
      )}


      {/* </Box>) */}

      {/* Results list */}
      {results.length > 0 && (
        <>
          <List disablePadding>
            {results.map((addr: any, idx: number) => {
              const note = addr.latest_note;

              return (
                <Box key={addr.id}>
                  <ListItemButton
                    onClick={() => {
                      primeAddressDetail(addr);
                      navigate({
                        pathname: `address/${addr.udprn}`,
                        search: searchParams.toString(),
                      });
                    }}
                    sx={{
                      backgroundColor: "white",
                      px: 2,
                      py: 1.2,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={600}>
                          {addr.to_display}
                        </Typography>
                      }
                      slotProps={{ secondary: { component: "div" } }}
                      secondary={
                        <Stack spacing={0.3} sx={{ mt: 0.3 }}>
                          {/* Customer name only — NO postcode */}
                          {addr.customer?.name && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.3 }}
                            >
                              {addr.customer.name}
                            </Typography>
                          )}

                          {/* Status + note — compact */}
                          {note && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.3, mt: addr.customer ? 0 : 0.2 }}
                            >
                              <strong>{note.status_display}</strong>
                              {note.note ? ` — ${note.note}` : ""}
                            </Typography>
                          )}

                          {/* Compact REGULAR / QUOTED metadata */}
                          {note &&
                            (note.status === "REGULAR" ||
                              note.status === "QUOTED") && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ lineHeight: 1.2 }}
                              >
                                {[
                                  note.frequency_display &&
                                    `Freq: ${note.frequency_display}`,
                                  note.payment_type_display &&
                                    `Pay: ${note.payment_type_display}`,
                                  note.price !== null && `£${note.price}`,
                                  note.notification_type_display &&
                                    `Notify: ${note.notification_type_display}`,
                                  note.followup_date &&
                                    `Follow-up: ${new Date(
                                      note.followup_date
                                    ).toLocaleDateString()}`,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </Typography>
                            )}

                          {/* Username + timestamp */}
                          {note && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ lineHeight: 1.2, mt: 0.2 }}
                            >
                              {note.username ? `${note.username} — ` : ""}
                              {new Date(note.created_at).toLocaleString()}
                            </Typography>
                          )}
                        </Stack>
                      }
                    />

                    <ArrowForwardIosIcon sx={{ fontSize: 16, opacity: 0.35 }} />
                  </ListItemButton>

                  {idx < results.length - 1 && <Divider sx={{ ml: 2 }} />}
                </Box>
              );
            })}
          </List>

          {hasNextPage && <Box ref={loadMoreRef} sx={{ height: 1 }} />}

          {isFetchingNextPage && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </>
      )}

 
      {/* // No results */}
 {typedFirstPage && !isLoading && results.length === 0 && (
   <Box textAlign="center" py={3}>
     <Typography color="text.secondary">
       No addresses match your filters.
     </Typography>
   </Box>
 )} 

       {/* Scroll to top button */}
       {showScrollTop && (
         <Fab
           color="primary"
           size="small"
           onClick={() =>
             window.scrollTo({ top: 0, behavior: "smooth" })
           }
           sx={{
             position: "fixed",
             bottom: 90,
             right: 16,
             zIndex: 1000,
           }}
         >
           <KeyboardArrowUpIcon />
         </Fab>
       )} 
         <Outlet/>
   </Box>
 );

}
